import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Boxes, ExternalLink, Settings2, Play } from 'lucide-react';
import { miniAppsApi, type PublicMiniApp } from '@/api/miniApps';
import MiniAppHost from '@/components/miniapp/MiniAppHost';

/**
 * `/miniapps` — каталог публичных мини-аппов + ссылка на «Мои мини-аппы».
 * Запуск из каталога происходит в STANDALONE scope, без чат-контекста.
 */
export default function MiniAppsPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState<PublicMiniApp | null>(null);

  const { data: directory = [], isLoading } = useQuery({
    queryKey: ['miniapps', 'directory'],
    queryFn: () => miniAppsApi.listDirectory(),
  });

  return (
    <div className="flex-1 flex flex-col bg-surface-950 text-surface-100">
      <div className="h-12 border-b border-surface-800 px-4 flex items-center gap-3">
        <button onClick={() => navigate('/')} className="btn-ghost p-1.5 text-surface-400">
          <ArrowLeft size={20} />
        </button>
        <Boxes size={18} className="text-brand-400" />
        <span className="font-semibold">Мини-аппы</span>
        <button
          onClick={() => navigate('/miniapps/manage')}
          className="ml-auto px-3 py-1.5 rounded-md bg-surface-800 hover:bg-surface-700 text-surface-100 text-sm flex items-center gap-1"
        >
          <Settings2 size={14} /> Мои мини-аппы
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && <div className="text-surface-500 text-sm">Загрузка…</div>}
        {!isLoading && directory.length === 0 && (
          <div className="text-surface-500 text-sm">
            В каталоге пока нет публичных мини-аппов. Создайте свой в «Мои мини-аппы».
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {directory.map((app) => (
            <div
              key={app.id}
              className="border border-surface-800 rounded-lg p-3 bg-surface-900 hover:border-brand-700 transition-colors"
            >
              <div className="flex items-start gap-3">
                {app.iconUrl ? (
                  <img src={app.iconUrl} alt="" className="w-10 h-10 rounded-md flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-surface-800 flex items-center justify-center flex-shrink-0">
                    <Boxes size={18} className="text-surface-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{app.displayName}</div>
                  <div className="text-xs text-surface-500 font-mono truncate">@{app.slug}</div>
                </div>
              </div>
              {app.shortDescription && (
                <p className="text-xs text-surface-400 mt-2 line-clamp-2">{app.shortDescription}</p>
              )}
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => setActive(app)}
                  className="flex-1 px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-500 text-white text-xs flex items-center justify-center gap-1"
                >
                  <Play size={12} /> Открыть
                </button>
                <a
                  href={app.url}
                  target="_blank"
                  rel="noreferrer"
                  className="px-2 py-1.5 rounded-md bg-surface-800 hover:bg-surface-700 text-surface-300"
                  title="Открыть исходник в новой вкладке"
                >
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {active && <MiniAppHost app={active} scope="STANDALONE" onClose={() => setActive(null)} />}
    </div>
  );
}
